import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import {
  User,
  Trophy,
  Target,
  TrendingUp,
  Star,
  Edit,
  Calendar,
  Save,
  Loader2,
  Bell,
  BellOff,
  Check,
} from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import { profileApi } from '@/services/api'
import { supabase } from '@/services/supabase'
import toast from 'react-hot-toast'
import { getTeamById as getStaticTeamById } from '@/data/teams'
import { useTeamsStore } from '@/stores/teamsStore'
import { AvatarCropModal } from '@/components/ui/AvatarCropModal'

// ─── Glass Card ──────────────────────────────────────────────

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] rounded-2xl shadow-lg ${className || ''}`}>
    {children}
  </div>
)

// ─── Types ───────────────────────────────────────────────────

interface ProfileData {
  userId: string
  displayName: string
  avatarUrl: string | null
  bio: string | null
  socialLinks: Record<string, string>
  notificationPrefs: Record<string, boolean>
  favoriteTeamId?: string | null
  createdAt: string
}

interface ProfileStats {
  totalBrackets: number
  submittedBrackets: number
  totalPicks: number
  correctPicks: number
  accuracy: number
}

// ─── Profile Page ────────────────────────────────────────────

export const ProfilePage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'settings'>('overview')

  // Profile data
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [stats, setStats] = useState<ProfileStats | null>(null)

  // Editable fields
  const [displayName, setDisplayName] = useState('')
  const [bio, setBio] = useState('')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({
    tournament_updates: true,
    bracket_results: true,
    league_activity: true,
  })
  const [favoriteTeamId, setFavoriteTeamId] = useState<string | null>(null)
  const [showTeamPicker, setShowTeamPicker] = useState(false)
  const [teamSearch, setTeamSearch] = useState('')
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [initialForm, setInitialForm] = useState({
    displayName: '',
    bio: '',
    notifPrefs: {
      tournament_updates: true,
      bracket_results: true,
      league_activity: true,
    } as Record<string, boolean>,
  })

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Fetch full team list from API store
  const fetchTeams = useTeamsStore(s => s.fetchTeams)
  const teamsById = useTeamsStore(s => s.teamsById)
  const allTeams = useMemo(() => {
    const seen = new Set<string>()
    return Object.values(teamsById).filter(t => {
      const key = (t.shortName || t.name || '').toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    }).sort((a, b) => (a.shortName || a.name).localeCompare(b.shortName || b.name))
  }, [teamsById])
  useEffect(() => { fetchTeams() }, [fetchTeams])
  const getTeamById = useCallback((id: string) => teamsById[id] ?? getStaticTeamById(id), [teamsById])

  const normalizeText = useCallback((value?: string | null) => (value ?? '').trim(), [])

  const normalizePrefs = useCallback((prefs?: Record<string, boolean>) => ({
    tournament_updates: prefs?.tournament_updates ?? true,
    bracket_results: prefs?.bracket_results ?? true,
    league_activity: prefs?.league_activity ?? true,
  }), [])

  const prefsFingerprint = useCallback((prefs: Record<string, boolean>) => {
    const ordered = Object.keys(prefs).sort().reduce((acc: Record<string, boolean>, key) => {
      acc[key] = Boolean(prefs[key])
      return acc
    }, {})
    return JSON.stringify(ordered)
  }, [])

  // Load profile
  useEffect(() => {
    let mounted = true
    profileApi.me().then((res) => {
      if (!mounted) return
      const p = res.profile
      const s = res.stats
      const normalizedPrefs = normalizePrefs(p.notificationPrefs)
      const normalizedDisplay = normalizeText(p.displayName)
      const normalizedBio = normalizeText(p.bio)

      setProfile(p)
      setStats(s)
      setDisplayName(normalizedDisplay)
      setBio(normalizedBio)
      setAvatarUrl(p.avatarUrl)
      setNotifPrefs(normalizedPrefs)
      setFavoriteTeamId(p.favoriteTeamId || null)
      setInitialForm({
        displayName: normalizedDisplay,
        bio: normalizedBio,
        notifPrefs: normalizedPrefs,
      })
      setDirty(false)
    }).catch(() => {
      if (!mounted) return
      toast.error('Failed to load profile')
    }).finally(() => {
      if (!mounted) return
      setLoading(false)
    })
    return () => { mounted = false }
  }, [normalizePrefs, normalizeText])

  // Track dirty state
  useEffect(() => {
    const isDirty =
      normalizeText(displayName) !== initialForm.displayName ||
      normalizeText(bio) !== initialForm.bio ||
      favoriteTeamId !== (profile?.favoriteTeamId || null) ||
      prefsFingerprint(normalizePrefs(notifPrefs)) !== prefsFingerprint(normalizePrefs(initialForm.notifPrefs))
    setDirty(isDirty)
  }, [displayName, bio, favoriteTeamId, notifPrefs, initialForm, profile, normalizePrefs, prefsFingerprint])

  // Save profile
  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      const normalizedDisplay = normalizeText(displayName)
      const normalizedBio = normalizeText(bio)
      const normalizedPrefs = normalizePrefs(notifPrefs)

      const res = await profileApi.updateMe({
        displayName: normalizedDisplay,
        bio: normalizedBio,
        notificationPrefs: normalizedPrefs,
        favoriteTeamId: favoriteTeamId,
      } as any)

      const updatedProfile = {
        ...res.profile,
        displayName: normalizeText(res.profile?.displayName) || normalizedDisplay,
        bio: normalizeText(res.profile?.bio) || normalizedBio,
        notificationPrefs: normalizePrefs(res.profile?.notificationPrefs ?? normalizedPrefs),
      }

      setProfile(updatedProfile)
      setDisplayName(updatedProfile.displayName)
      setBio(updatedProfile.bio)
      setNotifPrefs(updatedProfile.notificationPrefs)
      setInitialForm({
        displayName: updatedProfile.displayName,
        bio: updatedProfile.bio,
        notifPrefs: updatedProfile.notificationPrefs,
      })
      setDirty(false)
      toast.success('Profile saved')
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save profile')
    } finally {
      setSaving(false)
    }
  }, [displayName, bio, favoriteTeamId, notifPrefs, normalizePrefs, normalizeText])

  // Avatar: open crop modal after file select
  const handleAvatarSelected = (file?: File) => {
    if (!file) return
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return }
    const reader = new FileReader()
    reader.onload = () => { if (typeof reader.result === 'string') setCropImageSrc(reader.result) }
    reader.readAsDataURL(file)
  }

  // Avatar: upload cropped blob via API
  const handleCropDone = async (blob: Blob) => {
    setCropImageSrc(null)
    setUploadingAvatar(true)
    try {
      const formData = new FormData()
      formData.append('file', blob, 'avatar.jpg')

      const token = (await supabase.auth.getSession())?.data?.session?.access_token
      const apiBase = import.meta.env.VITE_API_URL || 'https://bracketblitz-production.up.railway.app/api/v1'
      const res = await fetch(`${apiBase}/profile/avatar`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: formData,
      })

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Avatar upload not available yet — backend redeploy needed')
        }
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.message || `Upload failed (${res.status})`)
      }

      const data = await res.json()
      const publicUrl = data?.data?.avatarUrl || data?.avatarUrl
      if (publicUrl) setAvatarUrl(publicUrl)
      toast.success('Avatar updated!')
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to upload avatar')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const formatDate = (d: string) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  // Notification toggle
  const toggleNotif = (key: string) => {
    setNotifPrefs(prev => ({ ...prev, [key]: !prev[key] }))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-white/30 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent px-4 py-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg">
            <User className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-white">Profile</h1>
            <p className="text-sm text-white/40">Manage your account and view stats</p>
          </div>
        </div>

        {/* Profile Card */}
        <GlassCard className="p-6">
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar */}
            <div className="relative">
              <Avatar
                size="xl"
                src={avatarUrl ?? undefined}
                alt={displayName || 'User'}
                fallback={(displayName || 'U').slice(0, 2).toUpperCase()}
              />
              <button
                className="absolute bottom-0 right-0 p-2 bg-white/[0.15] hover:bg-white/[0.25] rounded-full text-white transition-colors shadow-lg disabled:opacity-60"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                title={uploadingAvatar ? 'Uploading...' : 'Upload avatar'}
              >
                {uploadingAvatar ? <Loader2 className="h-4 w-4 animate-spin" /> : <Edit className="h-4 w-4" />}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { handleAvatarSelected(e.target.files?.[0]); e.target.value = '' }}
              />
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-white">{displayName || 'User'}</h2>
                {favoriteTeamId && getTeamById(favoriteTeamId) && (
                  <img src={getTeamById(favoriteTeamId)!.logo} alt={getTeamById(favoriteTeamId)!.shortName} className="w-7 h-7 rounded object-contain bg-white/10 p-0.5" title={getTeamById(favoriteTeamId)!.name} />
                )}
              </div>
              {profile?.createdAt && (
                <div className="flex items-center gap-1.5 text-sm text-white/40 mt-1">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Member since {formatDate(profile.createdAt)}</span>
                </div>
              )}
              {bio && <p className="text-sm text-white/50 mt-2">{bio}</p>}

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {[
                  { label: 'Brackets', value: stats?.totalBrackets ?? 0, icon: Target, color: 'text-blue-400' },
                  { label: 'Accuracy', value: `${stats?.accuracy ?? 0}%`, icon: TrendingUp, color: 'text-green-400' },
                  { label: 'Total Picks', value: stats?.totalPicks ?? 0, icon: Star, color: 'text-amber-400' },
                  { label: 'Correct', value: stats?.correctPicks ?? 0, icon: Trophy, color: 'text-purple-400' },
                ].map(item => (
                  <div key={item.label} className="text-center p-3 bg-white/[0.03] border border-white/[0.06] rounded-xl">
                    <item.icon className={`h-4 w-4 mx-auto mb-1 ${item.color}`} />
                    <div className="text-lg font-bold text-white">
                      {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                    </div>
                    <div className="text-[10px] text-white/30 uppercase tracking-wider">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </GlassCard>

        {/* Tab Navigation */}
        <div className="flex justify-center">
          <div className="bg-white/[0.04] border border-white/[0.08] p-1 rounded-xl inline-flex">
            {(['overview', 'settings'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab
                    ? 'bg-white/[0.1] text-white'
                    : 'text-white/40 hover:text-white/60'
                }`}
              >
                {tab === 'overview' ? 'Overview' : 'Settings'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Detailed Stats */}
            <GlassCard className="p-6">
              <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">Bracket Stats</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Total Brackets', value: stats?.totalBrackets ?? 0, icon: Target, color: 'text-blue-400' },
                  { label: 'Submitted', value: stats?.submittedBrackets ?? 0, icon: Check, color: 'text-green-400' },
                  { label: 'Total Picks', value: stats?.totalPicks ?? 0, icon: Star, color: 'text-amber-400' },
                  { label: 'Correct Picks', value: stats?.correctPicks ?? 0, icon: Trophy, color: 'text-purple-400' },
                  { label: 'Accuracy', value: `${stats?.accuracy ?? 0}%`, icon: TrendingUp, color: 'text-emerald-400' },
                  { label: 'Member Since', value: profile?.createdAt ? formatDate(profile.createdAt) : '—', icon: Calendar, color: 'text-white/40' },
                ].map(item => (
                  <div key={item.label} className="p-4 bg-white/[0.02] rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <item.icon className={`h-4 w-4 ${item.color}`} />
                      <span className="text-xs text-white/40 uppercase tracking-wider">{item.label}</span>
                    </div>
                    <div className="text-xl font-bold text-white">
                      {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6 max-w-2xl mx-auto">
            {/* Display Settings */}
            <GlassCard className="p-6">
              <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">Display Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-white/40 mb-1.5 block">Display Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    maxLength={80}
                    placeholder="Your display name"
                    className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/20"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/40 mb-1.5 block">Bio</label>
                  <textarea
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="Tell us about yourself..."
                    className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/20 resize-none"
                  />
                  <span className="text-[10px] text-white/20 mt-1 block">{bio.length}/500</span>
                </div>
              </div>
            </GlassCard>

            {/* Favorite Team */}
            <GlassCard className="p-6">
              <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">🏀 Favorite Team</h3>
              {favoriteTeamId && getTeamById(favoriteTeamId) ? (
                <div className="flex items-center gap-3 mb-3 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                  <img src={getTeamById(favoriteTeamId)!.logo} alt={getTeamById(favoriteTeamId)!.shortName} className="w-10 h-10 rounded-lg object-contain bg-white/10 p-1" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-white">{getTeamById(favoriteTeamId)!.shortName}</div>
                    <div className="text-[10px] text-white/30">{getTeamById(favoriteTeamId)!.conference} • Seed #{getTeamById(favoriteTeamId)!.seed}</div>
                  </div>
                  <button onClick={() => { setFavoriteTeamId(null); setShowTeamPicker(true) }} className="text-[10px] text-orange-400 hover:text-orange-300 mr-2">Change</button>
                  <button onClick={() => { setFavoriteTeamId(null); setShowTeamPicker(false) }} className="text-[10px] text-red-400/60 hover:text-red-400">Remove</button>
                </div>
              ) : (
                <button onClick={() => setShowTeamPicker(true)} className="w-full p-3 rounded-xl border-2 border-dashed border-white/[0.08] text-sm text-white/30 hover:text-white/50 hover:border-white/[0.15] transition-colors">+ Select your favorite team (optional)</button>
              )}
              {showTeamPicker && (
                <div className="mt-3">
                  <input type="text" value={teamSearch} onChange={(e) => setTeamSearch(e.target.value)} placeholder="Search teams..." autoFocus className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-orange-500/30 mb-2" />
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {allTeams.filter((t) => { const q = teamSearch.toLowerCase(); return !q || t.name.toLowerCase().includes(q) || t.shortName.toLowerCase().includes(q) || (t.mascot && t.mascot.toLowerCase().includes(q)) }).slice(0, 30).map((t) => (
                      <button key={t.id} onClick={() => { setFavoriteTeamId(t.id); setShowTeamPicker(false); setTeamSearch('') }} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.06] transition-colors text-left min-w-0">
                        <img src={t.logo} alt={t.shortName || t.name} className="w-7 h-7 flex-shrink-0 rounded object-contain bg-white/10 p-0.5" />
                        <div className="min-w-0 flex-1"><div className="text-xs font-medium text-white truncate">{t.shortName || t.name}</div><div className="text-[10px] text-white/25 truncate">{t.conference}</div></div>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => { setShowTeamPicker(false); setTeamSearch('') }} className="mt-2 text-[10px] text-white/30 hover:text-white/50">Cancel</button>
                </div>
              )}
            </GlassCard>

            {/* Notification Preferences */}
            <GlassCard className="p-6">
              <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Notification Preferences
              </h3>
              <p className="text-xs text-white/25 mb-4">Preferences are saved to your profile. Push notifications will be enabled in a future update.</p>
              <div className="space-y-3">
                {[
                  { key: 'tournament_updates', label: 'Tournament Updates', desc: 'Game results and bracket updates' },
                  { key: 'bracket_results', label: 'Bracket Results', desc: 'Scoring and ranking changes' },
                  { key: 'league_activity', label: 'League Activity', desc: 'League chat and member activity' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between py-2">
                    <div>
                      <div className="text-sm text-white/80">{item.label}</div>
                      <div className="text-xs text-white/30">{item.desc}</div>
                    </div>
                    <button
                      onClick={() => toggleNotif(item.key)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        notifPrefs[item.key]
                          ? 'bg-green-500/30 border border-green-500/50'
                          : 'bg-white/[0.06] border border-white/[0.1]'
                      }`}
                    >
                      <div className={`absolute top-0.5 h-5 w-5 rounded-full transition-all flex items-center justify-center ${
                        notifPrefs[item.key]
                          ? 'left-[22px] bg-green-400'
                          : 'left-0.5 bg-white/30'
                      }`}>
                        {notifPrefs[item.key]
                          ? <Bell className="h-2.5 w-2.5 text-white" />
                          : <BellOff className="h-2.5 w-2.5 text-white/50" />}
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={!dirty || saving}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  dirty
                    ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30'
                    : 'bg-white/[0.04] text-white/30 border border-white/[0.08] cursor-not-allowed'
                }`}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving...' : dirty ? 'Save Changes' : 'No Changes'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Avatar Crop Modal */}
      {cropImageSrc && (
        <AvatarCropModal
          imageSrc={cropImageSrc}
          onCropDone={handleCropDone}
          onCancel={() => setCropImageSrc(null)}
        />
      )}
    </div>
  )
}
